/**
 * The board's hero: the target at the top of the cumulative board, as a **standing**.
 *
 * ── Why this is not the run tab's versus band ────────────────────────────────────
 *
 * `leaderboard.targets` is one run's ranking: rank 1 and rank 2 genuinely played each
 * other in one tournament, so a head-to-head between them is a real comparison. The
 * board is not that. It is one row per (canonical protein, canonical mechanism),
 * derived from every run's ranking, so two rows here can come from two different
 * sweeps — different opponents, different pool, different Elo scale. A "leader vs
 * runner-up" band would therefore be drawing a duel between numbers that were never
 * compared, and it would be the most quotable thing on the page.
 *
 * **Do not add a versus band, a gap figure or a "beats" verb to this tab.** The
 * comparison the data supports is each rating against the 1500 every tournament
 * starts from, which is what `EloRange` and `EloStrip` draw.
 *
 * ── What "leader" is allowed to mean here ────────────────────────────────────────
 *
 * Positional, and it says so. The card shows the row the *current ordering* puts
 * first and names that ordering, because nothing in the data marks one board row as
 * the board's winner — `runs_identified` is a count of sweeps, `best_elo` is a
 * per-run rating, and a champions Elo is a third quantity again. Rendered only on the
 * first page: the top row of page three is the top of nothing.
 *
 * The Elo figures stay mono numbers and a range. Elo starts at 1500 with K=24 and is
 * unbounded, so there is no maximum to normalise a bar against — a percentage bar
 * would invent one, and it would rescale itself silently as rows changed.
 */
import { Card, CardBody, CardHeader } from './ui/Card'
import { Chip } from './ui/Chip'
import { TierBadge } from './StatusBadge'
import { cn, formatNumber, formatTimestamp } from '../lib/utils'
import type { BoardEntry, BoardSortColumn, SortDirection } from '../api/types'

/** Every tournament starts every hypothesis here, which is the one cross-run fact. */
const BASELINE = 1500

/**
 * How the card names the ordering that put this row first.
 *
 * A missing key renders as the raw column name rather than as nothing, so a column
 * added to `BOARD_SORT_COLUMNS` on the Python side shows up here instead of making
 * the sentence read as though no ordering were applied.
 */
const SORT_PHRASES: Partial<Record<BoardSortColumn, string>> = {
  runs_identified: 'runs that identified it',
  best_elo: 'best Elo',
  latest_elo: 'latest Elo',
  best_btl: 'best BTL',
  protein: 'protein name',
  best_tier: 'best tier',
  latest_tier: 'latest tier',
  pathway: 'pathway',
  first_seen_at: 'first seen',
  last_seen_at: 'last seen',
  champions_rounds: 'champions tournaments entered',
  champion_elo: 'champions Elo',
  ligand_start_count: 'orderable ligand analogs found',
}

/**
 * `worst → latest → best` as one labelled scale, with the 1500 baseline on it.
 *
 * A scale and not a bar. There is no maximum Elo, so there is nothing for a fill to
 * be a fraction *of*; what the reader can actually be told is where the latest rating
 * sits between the lowest and highest any run gave this target, and whether those
 * ratings are above or below the number every tournament starts from. So: a hairline,
 * two end caps, a dashed baseline tick and one mark for the latest — no fill anywhere.
 *
 * Exported because the drawer needs the same three numbers and a second copy of this
 * reasoning would drift from this one.
 */
export function EloRange({
  worst,
  latest,
  best,
  className,
}: {
  worst: number | null | undefined
  latest: number | null | undefined
  best: number | null | undefined
  className?: string
}) {
  const known = [worst, latest, best].filter(
    (v): v is number => v !== null && v !== undefined && !Number.isNaN(v),
  )

  // No Elo at all is a state of its own: the target was ranked but no tournament
  // rated it. Not a zero, and not an empty scale that would read as one.
  if (known.length === 0) {
    return (
      <p className={cn('text-2xs text-slate-500', className)}>
        No run recorded an Elo for this target.
      </p>
    )
  }

  // The baseline is always on the canvas, for the reason `EloStrip` keeps it there:
  // a target every run rated above 1500 would otherwise be drawn against a floor of
  // its own worst run and read as though it had been beaten.
  const lo = Math.min(...known, BASELINE)
  const hi = Math.max(...known, BASELINE)
  const span = hi - lo || 1
  const at = (v: number) => `${((v - lo) / span) * 100}%`

  return (
    <div className={className}>
      <div
        className="relative h-4"
        role="img"
        aria-label={`Elo range across the runs that ranked this target: worst ${formatNumber(
          worst,
        )}, latest ${formatNumber(latest)}, best ${formatNumber(
          best,
        )}. Every tournament starts at ${BASELINE}.`}
      >
        <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-slate-700" />
        {/* The baseline, dashed like the one in `EloStrip` so the two read as the
            same reference rather than as two different thresholds. */}
        <div
          className="absolute top-0 h-4 border-l border-dashed border-slate-600"
          style={{ left: at(BASELINE) }}
          title={`${BASELINE} — where every tournament starts every hypothesis`}
        />
        {[worst, best].map((v, i) =>
          v === null || v === undefined ? null : (
            <div
              key={i}
              className="absolute top-1/2 h-2 w-px -translate-y-1/2 bg-slate-500"
              style={{ left: at(v) }}
            />
          ),
        )}
        {latest !== null && latest !== undefined && (
          <div
            className={cn(
              'absolute top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full',
              latest >= BASELINE ? 'bg-brand-400' : 'bg-slate-400',
            )}
            style={{ left: at(latest) }}
          />
        )}
      </div>
      <div className="mt-1.5 flex items-baseline justify-between gap-3">
        <RangeFigure label="worst" value={worst} />
        <RangeFigure label="latest" value={latest} emphasis />
        <RangeFigure label="best" value={best} align="right" />
      </div>
    </div>
  )
}

function RangeFigure({
  label,
  value,
  emphasis = false,
  align = 'left',
}: {
  label: string
  value: number | null | undefined
  emphasis?: boolean
  align?: 'left' | 'right'
}) {
  return (
    <div className={align === 'right' ? 'text-right' : undefined}>
      <div className="font-display text-2xs uppercase tracking-eyebrow text-slate-500">
        {label}
      </div>
      <div
        className={cn(
          'font-mono tabular-nums',
          emphasis ? 'text-sm text-slate-100' : 'text-xs text-slate-400',
        )}
      >
        {formatNumber(value)}
      </div>
    </div>
  )
}

/** A quiet board-level figure. Label above, value below, no chrome. */
function Stat({
  label,
  value,
  hint,
}: {
  label: string
  value: string
  hint: string
}) {
  return (
    <div title={hint}>
      <div className="font-display text-2xs uppercase tracking-eyebrow text-slate-500">
        {label}
      </div>
      <div className="mt-0.5 font-mono text-sm tabular-nums text-slate-200">
        {value}
      </div>
    </div>
  )
}

interface Props {
  /** The rows currently loaded. `entries[0]` is the standing this card describes. */
  entries: BoardEntry[]
  /** Rows on the whole board, from the API — not `entries.length`. */
  total: number
  /** The API's `runs_identified_note`, rendered verbatim under the count. */
  note: string | null
  sort: BoardSortColumn
  direction: SortDirection
  /** Suppresses the card past page 1: the top row of page 3 leads nothing. */
  offset: number
}

export function BoardLeader({ entries, total, note, sort, direction, offset }: Props) {
  const entry = entries[0]
  if (!entry || offset > 0) return null

  // Both derived from the rows in memory, so both say so. The API sends no
  // board-wide sweep count and no board build stamp, and a figure presented as
  // whole-board when it is page-wide is the kind of quiet overstatement this UI
  // exists to avoid.
  const sweeps = new Set(entries.flatMap((e) => e.run_ids ?? [])).size
  // ISO-8601 sorts lexicographically, so the last element is the newest. No `.at(-1)`
  // — the tsconfig's lib target predates it and `tsc` rejects it.
  const stamps = entries
    .map((e) => e.updated_at)
    .filter((v): v is string => !!v)
    .sort()
  const vintage = stamps.length > 0 ? stamps[stamps.length - 1] : null

  // `latest_btl` is `numeric` in Postgres, which psycopg hands over as Decimal and
  // the API stringifies to keep the precision — so this field is a string on some
  // rows and a number on others, and both are correct. Coerced explicitly rather
  // than compared or formatted as-is.
  const btlRaw = entry.latest_btl
  const btl = btlRaw === null || btlRaw === undefined ? null : Number(btlRaw)

  const ordering = SORT_PHRASES[sort] ?? sort

  return (
    <Card className="animate-rise">
      <CardHeader
        eyebrow="Board standing"
        title={entry.protein}
        description={
          <>
            Top of the cumulative board as currently ordered by{' '}
            <span className="text-slate-300">{ordering}</span>,{' '}
            {direction === 'desc' ? 'highest first' : 'lowest first'}. A standing, not
            a head-to-head: board rows come from different sweeps, so their ratings
            were never compared with each other.
          </>
        }
        actions={
          <div className="flex flex-col items-end gap-1">
            <TierBadge tier={entry.latest_tier} />
            {entry.best_tier && entry.best_tier !== entry.latest_tier && (
              <span
                className="text-2xs text-slate-500"
                title="Two runs reached different conclusions about this target. Both are kept; neither is the board's answer."
              >
                best: {entry.best_tier}
              </span>
            )}
          </div>
        }
      />
      <CardBody className="space-y-5">
        <div className="flex flex-wrap items-baseline gap-2">
          {entry.accession && (
            <Chip font="mono" emphasis>
              {entry.accession}
            </Chip>
          )}
          <span className="text-sm text-slate-300">{entry.moa ?? 'No mechanism recorded'}</span>
          {entry.pathway && <Chip>{entry.pathway}</Chip>}
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <div className="font-display text-2xs uppercase tracking-eyebrow text-slate-500">
              Elo across the runs that ranked it
            </div>
            <EloRange
              className="mt-2 max-w-xs"
              worst={entry.worst_elo}
              latest={entry.latest_elo}
              best={entry.best_elo}
            />
            <p className="mt-2 max-w-xs text-2xs leading-relaxed text-slate-500">
              Elo starts at {BASELINE} with K=24 and has no ceiling, so this is a
              range rather than a score out of anything. Each rating is comparative
              only within the run that produced it.
              {btl !== null && !Number.isNaN(btl) && (
                <>
                  {' '}
                  The latest run’s BTL strength was{' '}
                  <span className="font-mono tabular-nums text-slate-400">
                    {btl.toFixed(4)}
                  </span>
                  , likewise within that run’s pool only.
                </>
              )}
            </p>
          </div>

          <div>
            <div className="font-display text-2xs uppercase tracking-eyebrow text-slate-500">
              Runs that identified it
            </div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="font-display text-3xl font-semibold tabular-nums text-slate-100">
                {entry.runs_identified}
              </span>
              <span className="text-xs text-slate-400">
                across {entry.distinct_prompts} distinct prompt
                {entry.distinct_prompts === 1 ? '' : 's'} and{' '}
                {entry.distinct_threads} thread
                {entry.distinct_threads === 1 ? '' : 's'}
              </span>
            </div>
            {/* The server's own sentence, in full and on the read. Not a paraphrase,
                not truncated, and not only a tooltip: it is the qualifier without
                which the number above it reads as replication, and it travels with
                every place that number is shown — here and over the table. */}
            {note && (
              <p className="mt-2 max-w-md text-2xs leading-relaxed text-slate-400">
                {note}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 border-t border-slate-800 pt-4 sm:grid-cols-3">
          <Stat
            label="Targets on the board"
            value={String(total)}
            hint="Rows on the whole board, from the API — one per canonical protein and mechanism, not per run."
          />
          <Stat
            label="Sweeps behind these rows"
            value={String(sweeps)}
            hint="Distinct run ids across the rows loaded on this page. The API sends no board-wide sweep count, so this is page-wide and not the whole board's."
          />
          <Stat
            label="Board vintage"
            value={formatTimestamp(vintage)}
            hint="The newest update among the rows on this page. The board is rebuilt from every run's ranking on boot, so a figure taken a week apart is a different board."
          />
        </div>
      </CardBody>
    </Card>
  )
}
